import { get } from 'lodash';
import { SeverityDetails } from '../../containers/prescription/service';
import { Interactions, MedicinesInteractions } from '../../interfaces/medicine-drug-interactions';
import { themeColors } from '../../shared/theme/themeColors';

export const InteractionsHelpers = {
  getColorsInteractions(): {
    [key: string]: { label: string; backgroundColor: string; fontColor: string; borderColor: string };
  } {
    return {
      CONTRAINDICATED: {
        label: 'contraindicado',
        backgroundColor: themeColors.error.dark,
        fontColor: themeColors.error.lightest,
        borderColor: themeColors.error.light,
      },
      MAJOR: {
        label: 'grave',
        backgroundColor: themeColors.error.lightest,
        fontColor: themeColors.error.dark,
        borderColor: themeColors.error.light,
      },
      MODERATE: {
        label: 'moderada',
        backgroundColor: themeColors.warning.lightest,
        fontColor: themeColors.warning.dark,
        borderColor: themeColors.warning.light,
      },
      MINOR: {
        label: 'leve',
        backgroundColor: themeColors.alert.lightest,
        fontColor: themeColors.alert.dark,
        borderColor: themeColors.alert.light,
      },
      UNKNOWN: {
        label: 'desconhecido',
        backgroundColor: themeColors.alert.lightest,
        fontColor: themeColors.alert.dark,
        borderColor: themeColors.alert.light,
      },
    };
  },

  getSeverity(): string[] {
    return ['CONTRAINDICATED', 'MAJOR', 'MODERATE', 'MINOR', 'NONE'];
  },

  getTypes(): string[] {
    return ['PREGNANCY', 'LACTATION', 'FOOD', 'LAB', 'ETHANOL', 'TOBACCO', 'INGREDIENT_DUPLICATION'];
  },

  sortBySeverity(interactions: Interactions[]): Interactions[] {
    const severity = this.getSeverity();
    return interactions.sort(
      (a: Interactions, b: Interactions) => severity.indexOf(a.severity) - severity.indexOf(b.severity)
    );
  },

  sortBySeverityNested(interactions: { [key: string]: Interactions[] }): { [key: string]: Interactions[] } {
    const severity = this.getSeverity();
    interactions['PREGNANCY'] = this.sortBySeverity(interactions['PREGNANCY']);
    interactions['LACTATION'] = this.sortBySeverity(interactions['LACTATION']);
    interactions['FOOD'] = this.sortBySeverity(interactions['FOOD']);
    interactions['LAB'] = this.sortBySeverity(interactions['LAB']);
    interactions['ETHANOL'] = this.sortBySeverity(interactions['ETHANOL']);
    interactions['TOBACCO'] = this.sortBySeverity(interactions['TOBACCO']);
    interactions['INGREDIENT_DUPLICATION'] = this.sortBySeverity(interactions['INGREDIENT_DUPLICATION']);

    const orderBySeverity = Object.entries(interactions).map(([type, interactionsFromType]) => {
      if (interactionsFromType.length) {
        return { type, severity: interactionsFromType[0].severity };
      }
      return { type, severity: 'NONE' };
    });

    const order = orderBySeverity
      .sort((a, b) => severity.indexOf(a.severity) - severity.indexOf(b.severity))
      .map((order) => order.type);

    const orderedInteractions = Object.keys(interactions)
      .sort((a, b) => order.indexOf(a) - order.indexOf(b))
      .reduce((obj: { [key: string]: Interactions[] }, key: string) => {
        obj[key] = interactions[key];
        return obj;
      }, {});

    return orderedInteractions;
  },

  mergeSameInteractions(otherInteractions: Interactions[]): { [key: string]: Interactions[] } {
    let interactions: { [key: string]: Interactions[] } = {
      PREGNANCY: [],
      LACTATION: [],
      FOOD: [],
      LAB: [],
      ETHANOL: [],
      TOBACCO: [],
      INGREDIENT_DUPLICATION: [],
      PRECAUTIONS: [],
    };

    otherInteractions.forEach((otherInteraction) => {
      interactions[otherInteraction.type].push(otherInteraction);
    });

    return this.sortBySeverityNested(interactions);
  },
};

export const transformInteractionToSeverityDetails = (interactions: MedicinesInteractions[]): SeverityDetails[] => {
  return interactions.reduce<SeverityDetails[]>((accumulator, medicineDrugInteraction) => {
    medicineDrugInteraction.drugInteractions.forEach(
      ({ allergyInteractions, otherInteractions, medicineDrugInteractions }) => {
        allergyInteractions.forEach((allergyInteraction) =>
          accumulator.push({
            idPrimeiroMedicamento: null,
            idSegundomedicamento: null,
            severidade: allergyInteraction.severity,
            tipo: allergyInteraction.type,
            textoAviso: get(allergyInteraction, 'warningText[0]', ''),
          })
        );

        otherInteractions.forEach((otherInteractions) =>
          accumulator.push({
            idPrimeiroMedicamento: null,
            idSegundomedicamento: null,
            severidade: otherInteractions.severity,
            tipo: otherInteractions.type,
            textoAviso: get(otherInteractions, 'warningText[0]', ''),
          })
        );

        medicineDrugInteractions.forEach((interaction) => {
          interaction.drugInteraction.forEach((drugInter) => {
            return accumulator.push({
              idPrimeiroMedicamento: medicineDrugInteraction.medicineId || null,
              idSegundomedicamento: Number(interaction.medicineId) || null,
              severidade: drugInter.severity,
              tipo: drugInter.type,
              textoAviso: get(drugInter, 'warningText[0]', ''),
            });
          });
        });
      }
    );
    return accumulator;
  }, []);
};
